import { trackLLMGeneration } from './analyticsService';
import { getAuthHeaders } from './apiClient';
import {
  LUFA_PARSE_PROMPT,
  cleanGeminiResponse,
  extractDeliveryDate,
  partitionFoodItems,
  ParsedDeliveryItem,
  ParsedDeliveryResult,
} from '../utils/lufaDeliveryParse';
import { getApiBaseUrl } from '../config/apiBase';

// The parsing itself lives in ../utils/lufaDeliveryParse so the inbound-email
// handler can reuse it server-side. Re-exported here for existing importers.
export type { ParsedDeliveryItem, ParsedDeliveryResult };
export { extractDeliveryDate };

export async function parseLufaDeliveryEmail(emailText: string): Promise<ParsedDeliveryResult> {
  if (!emailText || emailText.trim().length === 0) {
    return { items: [], deliveryDate: null, error: 'Empty email text provided' };
  }

  const deliveryDate = extractDeliveryDate(emailText);

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getApiBaseUrl()}/api/ai/generate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userMessage: LUFA_PARSE_PROMPT + emailText }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(err.error || `Server returned ${response.status}`);
    }

    const data = await response.json();
    const responseText = data.result;

    trackLLMGeneration({
      $ai_trace_id: Math.random().toString(36).slice(2) + Date.now().toString(36),
      $ai_span_name: 'lufa_email_parse',
      $ai_model: data.model,
      $ai_provider: 'google',
      $ai_input_tokens: data.input_tokens,
      $ai_output_tokens: data.output_tokens,
      $ai_latency: data.latency_s,
    });
    const cleaned = cleanGeminiResponse(responseText);
    const items: ParsedDeliveryItem[] = JSON.parse(cleaned);

    // Post-process: drop charges and non-edible products the model let through
    const { food } = partitionFoodItems(items);

    return { items: food, deliveryDate };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown parsing error';
    return { items: [], deliveryDate, error: message };
  }
}
